/**
 * Types for the SUPER_USER-only optimization job logs endpoint
 * (GET /v4/optimizations/{optimizationId}/logs). The wire format is snake_case;
 * the response case interceptor converts it to camelCase before it reaches us,
 * so the response/query models below use camelCase.
 */

/** Cloud Logging severities (superset the UI handles). */
export type LogSeverity =
  | 'DEFAULT'
  | 'DEBUG'
  | 'INFO'
  | 'NOTICE'
  | 'WARNING'
  | 'ERROR'
  | 'CRITICAL'
  | 'ALERT'
  | 'EMERGENCY';

/** One log line. */
export interface OptimizationLogEntry {
  /** ISO-8601 timestamp, or null if the entry had none. */
  timestamp: string | null;
  /** Severity string; unknown/absent comes back as 'DEFAULT'. */
  severity: LogSeverity | string;
  /** The log message (may be multi-line or JSON text). */
  message: string;
}

/** 200 response body. `next_page_token` on the wire → `nextPageToken` here. */
export interface OptimizationLogsResponse {
  entries: OptimizationLogEntry[];
  /** Present when more pages exist; feed back as `page_token`. */
  nextPageToken?: string;
}

/**
 * Query params. Sent as snake_case on the wire (query params are NOT converted
 * by the request interceptor — the service writes the snake_case keys itself).
 */
export interface OptimizationLogsQuery {
  /** ISO-8601 upper bound of the window. Advanced override; usually omit. */
  endTime?: string;
  /** Max entries per page. Default 200. */
  pageSize?: number;
  /** Cursor from a previous response's `nextPageToken`. */
  pageToken?: string;
}

/**
 * Severity → design-system color variant, per the log viewer spec. Unknown or
 * absent severities fall back to a neutral variant.
 */
export type LogSeverityVariant = 'danger' | 'warning' | 'info' | 'muted' | 'neutral';

export function severityVariant(severity: string): LogSeverityVariant {
  switch (severity?.toUpperCase()) {
    case 'EMERGENCY':
    case 'ALERT':
    case 'CRITICAL':
    case 'ERROR':
      return 'danger';
    case 'WARNING':
    case 'NOTICE':
      return 'warning';
    case 'INFO':
      return 'info';
    case 'DEBUG':
      return 'muted';
    default:
      return 'neutral';
  }
}

/** All severities the filter offers, ordered from most to least severe. */
export const LOG_SEVERITIES: LogSeverity[] = [
  'EMERGENCY',
  'ALERT',
  'CRITICAL',
  'ERROR',
  'WARNING',
  'NOTICE',
  'INFO',
  'DEBUG',
  'DEFAULT'
];
